import React, {
	useCallback,
	type KeyboardEvent,
	memo,
	type ChangeEvent,
	useImperativeHandle,
} from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import akTextfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { TextCellWrapper } from '@atlassian/jira-business-fields/src/common/cell-wrapper/index.tsx';
import { FixItErrorFlag } from '@atlassian/jira-business-fields/src/common/ui/fixit-error-flag/index.tsx';
import { useEditField } from '@atlassian/jira-issue-field-base/src/services/edit-field-service/main.tsx';
import messages from './messages.tsx';
import type { NumberCellProps, NumberType } from './types.tsx';

const getInputValue = ({
	inputValue,
	fieldValue,
}: {
	inputValue?: string;
	fieldValue: NumberType | null;
}) => {
	if (typeof inputValue === 'undefined') {
		return fieldValue;
	}
	return typeof inputValue === 'string' && inputValue ? Number(inputValue) : null;
};

const NumberCell = memo<NumberCellProps>(
	({
		issueKey,
		fieldKey,
		fieldType = '',
		isEditable,
		fieldValue = null,
		fieldName,
		editMode = false,
		setEditMode = noop,
		cellRef,
		issueType = '',
		issueTypeId = '',
		inputValue,
		onInputChange = noop,
	}: NumberCellProps) => {
		const numValue = getInputValue({ inputValue, fieldValue });

		const setNumValue = useCallback(
			(value: NumberType) => onInputChange(value !== null ? String(value) : ''),
			[onInputChange],
		);

		const [{ value, error }, { saveValue, resetError }] = useEditField<NumberType, null>({
			fieldKey,
			issueKey,
			fieldType,
			initialValue: fieldValue,
		});
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const saveAndClose = useCallback(() => {
			if (editMode) {
				setEditMode(false);
				numValue !== value && saveValue(numValue, null, createAnalyticsEvent({}));
			}
		}, [createAnalyticsEvent, editMode, numValue, saveValue, value, setEditMode]);

		const handleChange = useCallback(
			(event: ChangeEvent<HTMLInputElement>) => {
				resetError();
				const typedValue =
					event.target.value === '' ? null : parseFloat(Number(event.target.value).toFixed(3));
				setNumValue(typedValue);
			},
			[resetError, setNumValue],
		);

		const handleKeyDownNew = useCallback(
			(event: KeyboardEvent<HTMLElement>) => {
				if (editMode && event.code === 'Escape') {
					setNumValue(value || null);
				}
				return false;
			},
			[editMode, setNumValue, value],
		);

		const cellOverrides = useCallback(() => ({ onKeyDown: handleKeyDownNew }), [handleKeyDownNew]);
		useImperativeHandle(cellRef, cellOverrides, [cellOverrides]);

		const numberCell = (
			<>
				{editMode ? (
					<form onSubmit={saveAndClose}>
						<Textfield
							autoFocus
							type="number"
							onBlur={saveAndClose}
							value={numValue === null ? '' : numValue}
							onChange={handleChange}
							step="any"
						/>
					</form>
				) : (
					<NumberLabel>{value}</NumberLabel>
				)}
				{error && (
					<FixItErrorFlag
						error={error}
						title={messages.errorTitle}
						description={messages.errorMessage}
						issueType={issueType}
						issueTypeId={issueTypeId}
						fieldId={fieldKey}
						fieldName={fieldName}
					/>
				)}
			</>
		);

		return (
			<TextCellWrapper
				testId="business-list.ui.list-view.number-cell.text-field-wrapper"
				isEditable={isEditable}
				ariaLabel={fieldName}
			>
				{numberCell}
			</TextCellWrapper>
		);
	},
);

export default NumberCell;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const NumberLabel = styled.span({
	textAlign: 'right',
	paddingRight: token('space.100'),
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Textfield = styled(akTextfield)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> [data-ds--text-field--input]': {
		textAlign: 'right',
		paddingRight: token('space.100'),
	},
});
